import React, { useState, FC } from 'react';
import { setClientApiUrl, resetApiUrl } from 'core/Api';

export interface IClientProps {
  id: string | null;
  name: string | null;
  url: string | null;
  type: string | null;
  templates: boolean | null;
  is_multilingual: boolean | null;
  leaves: boolean | null;
  is_form_76: boolean | null;
  permissions_scope?: boolean | null;
  is_hourly_absences?: boolean | null;
  is_health_and_safety_training?: boolean | null;
  is_work_trips?: boolean | null;
}

interface IContextState {
  client?: IClientProps;
  setActiveClient: ((props: IClientProps) => void) | null;
  removeActiveClient: (() => void) | null;
}

let activeClientSnapshot: IClientProps | null = null;

export const getActiveClient = (): IClientProps | null => {
  return activeClientSnapshot;
};

export const ActiveClientContext = React.createContext<IContextState>({
  client: {
    id: null,
    name: null,
    url: null,
    type: null,
    templates: null,
    is_multilingual: null,
    leaves: null,
    is_form_76: null,
    permissions_scope: null,
    is_hourly_absences: null,
    is_health_and_safety_training: null,
    is_work_trips: null,
  },
  setActiveClient: null,
  removeActiveClient: null,
});

interface IProps {
  children: React.ReactNode;
}

export const ActiveClientProvider: FC<IProps> = ({ children }: IProps): JSX.Element => {
  const [client, setClient] = useState<IClientProps>({
    id: null,
    name: null,
    url: null,
    type: null,
    templates: null,
    is_multilingual: null,
    leaves: null,
    is_form_76: null,
    permissions_scope: null,
    is_hourly_absences: null,
    is_health_and_safety_training: null,
    is_work_trips: null,
  });

  const setActiveClient = ({
    id,
    name,
    url,
    type,
    templates,
    is_multilingual,
    leaves,
    is_form_76,
    permissions_scope,
    is_hourly_absences,
    is_health_and_safety_training,
    is_work_trips,
  }: IClientProps) => {
    const newClient: IClientProps = {
      id,
      name,
      url,
      type,
      templates,
      is_multilingual,
      leaves,
      is_form_76,
      permissions_scope,
      is_hourly_absences,
      is_health_and_safety_training,
      is_work_trips,
    };

    sessionStorage.setItem('ehr_active_client_id', newClient.id || '');
    activeClientSnapshot = newClient;

    if (url) setClientApiUrl(url);

    return setClient(newClient);
  };

  const removeActiveClient = () => {
    // Reset the base URL back to the main one
    resetApiUrl();

    activeClientSnapshot = null;

    return setClient({
      id: null,
      name: null,
      url: null,
      type: null,
      templates: null,
      is_multilingual: null,
      leaves: null,
      is_form_76: null,
      permissions_scope: null,
      is_hourly_absences: null,
      is_health_and_safety_training: null,
      is_work_trips: null,
    });
  };

  return (
    <ActiveClientContext.Provider
      value={{
        client,
        setActiveClient,
        removeActiveClient,
      }}
    >
      <>{children}</>
    </ActiveClientContext.Provider>
  );
};

export function useActiveClientContext(): IContextState {
  const context = React.useContext(ActiveClientContext);
  if (context === undefined) {
    throw new Error('useActiveClientContext must be used within ActiveClientProvider');
  }

  return context;
}
